import Head from 'next/head';
import { useRouter } from 'next/router';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { MetaEnum } from '@/types/translations/MetaEnum';
import { ImagesBlogBlogThumbnailJpeg } from '@/assets/images_blog.assets';
import { AuthorMetaProps } from '@/types/props/components/blogs';

export default function AuthorMeta({
  authorName,
  authorSlug,
  authorImage,
  authorDescription,
}: AuthorMetaProps) {
  const { locale: localeParam = 'en', defaultLocale } = useRouter();
  const { translate } = useTypedTranslation<MetaEnum>('meta');

  const locale = localeParam === defaultLocale ? 'en' : localeParam;
  const formattedDate = new Date().toISOString();
  const authorProfileURL = `https://spiritory.com/${locale}/blog/authors/${authorSlug}`;
  const authorImageURL =
    authorImage || `https://spiritory.com${ImagesBlogBlogThumbnailJpeg.src}`;
  const authorFirstName =
    authorName.split(' ').length > 1 ? authorName.split(' ')[0] : authorName;
  const authorLastName =
    authorName.split(' ').length > 1 ? authorName.split(' ')[1] : '';

  return (
    <Head>
      <title>
        {translate(MetaEnum['single_author.author_profile_title']).replace(
          '{author_name}',
          authorName
        )}
      </title>

      {/* Essential SEO Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta
        name="keywords"
        content={translate(
          MetaEnum['single_author.author_profile_keywords']
        ).replace('{author_name}', authorName)}
      />
      <meta
        name="description"
        content={translate(
          MetaEnum['single_author.author_profile_description']
        ).replace('{author_name}', authorName)}
      />
      <meta
        name="subject"
        content={translate(MetaEnum['single_author.author_profile_subject'])}
      />
      <meta
        name="category"
        content={translate(MetaEnum['single_author.author_profile_category'])}
      />
      <meta
        name="subtitle"
        content={translate(MetaEnum['single_author.author_profile_subtitle'])}
      />
      <meta name="language" content={locale.toUpperCase()} />

      {/* Author & Copyright Meta Tags */}
      <meta name="author" content="Spiritory Team, info@spiritory.com" />
      <meta name="designer" content="Spiritory Development Team" />
      <meta name="copyright" content="Spiritory.com" />
      <meta name="owner" content="Spiritory" />
      <meta name="reply-to" content="info@spiritory.com" />

      {/* URL Meta Tags */}
      <meta name="url" content={authorProfileURL} />
      <meta name="identifier-URL" content={authorProfileURL} />

      {/* Coverage & Distribution */}
      <meta name="coverage" content="Worldwide" />
      <meta name="distribution" content="Global" />

      {/* Rating & Revisit Meta Tags */}
      <meta name="rating" content="General" />
      <meta name="revisit-after" content="7 days" />

      {/* Mobile Optimization */}
      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />

      {/* OpenGraph Meta Tags */}
      <meta
        property="og:title"
        content={translate(
          MetaEnum['single_author.author_profile_og_title']
        ).replace('{author_name}', authorName)}
      />
      <meta property="og:type" content="profile" />
      <meta property="og:url" content={authorProfileURL} />
      <meta property="og:image" content={authorImageURL} />
      <meta property="og:site_name" content="Spiritory" />
      <meta
        property="og:description"
        content={translate(
          MetaEnum['single_author.author_profile_description']
        ).replace('{author_name}', authorName)}
      />
      <meta
        property="profile:first_name"
        content={authorFirstName.split(' ')[0]}
      />
      <meta property="profile:last_name" content={authorLastName} />
      <meta property="og:locale" content={locale} />
      <meta property="og:updated_time" content={formattedDate} />

      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content={translate(
          MetaEnum['single_author.author_profile_title']
        ).replace('{author_name}', authorName)}
      />
      <meta
        name="twitter:description"
        content={translate(
          MetaEnum['single_author.author_profile_twitter_description']
        ).replace('{author_name}', authorName)}
      />
      <meta name="twitter:image" content={authorImageURL} />
      <meta name="twitter:site" content="@spiritoryGlobal" />
      <meta name="twitter:creator" content="@spiritoryGlobal" />

      {/* JSON-LD Schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: authorName,
            jobTitle: 'Whisky Expert & Blogger',
            description: authorDescription,
            url: authorProfileURL,
            image: authorImageURL,
            sameAs: [authorProfileURL],
            publisher: {
              '@type': 'Organization',
              name: 'Spiritory',
              logo: {
                '@type': 'ImageObject',
                url: '/logo.png',
                width: 474,
                height: 474,
              },
            },
            datePublished: '2025-01-14',
            dateModified: formattedDate,
          }),
        }}
      />

      {/* Multi-language SEO (Alternate Links) */}
      <link
        rel="alternate"
        hrefLang="es"
        href={`https://spiritory.com/es/blog/authors/${authorSlug}`}
      />
      <link
        rel="alternate"
        hrefLang="en"
        href={`https://spiritory.com/en/blog/authors/${authorSlug}`}
      />
      <link
        rel="alternate"
        hrefLang="de"
        href={`https://spiritory.com/de/blog/authors/${authorSlug}`}
      />
      <link
        rel="alternate"
        hrefLang="it"
        href={`https://spiritory.com/it/blog/authors/${authorSlug}`}
      />
      <link
        rel="alternate"
        hrefLang="fr"
        href={`https://spiritory.com/fr/blog/authors/${authorSlug}`}
      />

      {/* Canonical & Favicon */}
      <link rel="canonical" href={authorProfileURL} />
      <link rel="shortcut icon" href="/favicon.ico" type="image/x-icon" />
    </Head>
  );
}
